import React from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import PersonRow from "./PersonRow";
import { useTheme } from "../ThemeProvider";
import { getCategoryColor, getThemeColor } from "./seliseColors";
import ReorderContainer from "./ReorderContainer";

const ROLE_ORDER = ["VP", "Director", "Head", "Manager"];
const FIXED_ROLES = ["VP", "Director", "Head", "Manager"];
const ROLE_LABELS = { VP: "Vice President" };

export default function DivisionCard({
  division,
  people,
  isAdmin,
  onEditNode,
  onAddPerson,
  onDeleteNode,
  cSuiteNodes,
  onView,
  onReorderPeople,
  allPeople,
}) {
  const { theme } = useTheme();
  const groupedPeople = people.reduce((groups, person) => {
    let role = person.role_level || "Other";
    if (!groups[role]) groups[role] = [];
    groups[role].push(person);
    return groups;
  }, {});

  const rolesToShow = ROLE_ORDER.filter(
    (role) =>
      FIXED_ROLES.includes(role) ||
      (groupedPeople[role] && groupedPeople[role].length > 0)
  );

  const headerColor = getThemeColor(division.color, theme, getCategoryColor(division.category));

  return (
    <div
      className="bg-white rounded-xl border border-gray-200/80 flex flex-col group relative overflow-visible transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-gray-300 h-full"
      style={{ minWidth: "320px", maxWidth: "320px", boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 12px -2px rgba(0,0,0,0.06)" }}
    >
      {isAdmin && (
        <div className="absolute -top-2 -right-2 flex gap-1 z-10">
          <button
            onClick={() => onEditNode(division)}
            title="Edit Unit"
            className="p-1.5 bg-white rounded-full shadow border border-gray-200 text-gray-600 hover:text-blue-600"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDeleteNode(division.id)}
            title="Delete Unit"
            className="p-1.5 bg-white rounded-full shadow border border-gray-200 text-gray-600 hover:text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Title bar at top */}
      <div
        className="px-3 py-2 rounded-t-xl flex items-center justify-between"
        style={{ backgroundColor: headerColor }}
      >
        <div>
          <p className="text-white text-xs font-bold leading-tight">
            {division.department}
          </p>
          <p className="text-white text-[9px] uppercase opacity-80 tracking-wide" style={{ lineHeight: "1.2" }}>
            {division.category}
            {division.unit_size && division.unit_size !== "N/A" && (
              <span style={{ marginLeft: "6px" }}>
                <span className="text-white/40">|</span>
                <span className="normal-case tracking-normal font-semibold" style={{ marginLeft: "4px" }}>{division.unit_size}</span>
              </span>
            )}
          </p>
        </div>
        {division.department_code && (
          <span className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/20 ml-2">
            {division.department_code}
          </span>
        )}
      </div>

      <div className="p-3 flex-1">
        {rolesToShow
          .filter((role) => groupedPeople[role] && groupedPeople[role].length > 0)
          .map((role) => (
          <div key={role} className="mb-3 last:mb-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">
              {ROLE_LABELS[role] || role}
            </p>
            <ReorderContainer
              items={groupedPeople[role]}
              onReorder={onReorderPeople}
              droppableId={`people-${division.id}-${role}`}
              direction="vertical"
              disabled={!isAdmin}
              className=""
              renderItem={(person, index, dragProvided, dragSnapshot) => (
                <div
                  ref={dragProvided?.innerRef}
                  {...dragProvided?.draggableProps}
                  {...dragProvided?.dragHandleProps}
                  className={`${isAdmin ? "cursor-grab active:cursor-grabbing" : ""} ${dragSnapshot?.isDragging ? "ring-2 ring-blue-400 shadow-xl opacity-80" : ""}`}
                >
                  <PersonRow
                    person={person}
                    isAdmin={isAdmin}
                    onEdit={onEditNode}
                    onDelete={onDeleteNode}
                    cSuiteNodes={cSuiteNodes}
                    onView={onView}
                    allPeople={allPeople}
                  />
                </div>
              )}
            />
          </div>
        ))}
        {people.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-4">No members</p>
        )}
        {isAdmin && (
          <button
            onClick={onAddPerson}
            className="w-full mt-2 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-400 hover:text-blue-600 border border-dashed border-gray-300 rounded-md hover:border-blue-400 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
    </div>
  );
}